import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Check, ChevronsUpDown, Link2 } from "lucide-react";
import { useLocation } from "wouter";

const BLOCKED_LABEL: Record<string, string> = {
  inactive: "conta desativada",
  superadmin: "indisponível",
  workspace: "workspace suspenso",
};

/**
 * Indicador do workspace atual + troca rápida entre contas vinculadas.
 *
 * O nome do workspace vem do auth.me (que já carrega o tenant) — zero query
 * extra. A lista de contas vinculadas só é buscada quando o menu abre: a
 * maioria dos usuários não tem vínculo e não deve pagar essa query em toda
 * navegação.
 *
 * A troca emite o cookie de sessão da conta alvo no servidor; o reload total
 * (window.location) remonta o app inteiro com a nova sessão. Se a conta alvo
 * tem verificação em duas etapas, o servidor responde TOTP_REQUIRED e pedimos
 * o código antes de repetir.
 */
export function WorkspaceSwitcher() {
  const [, setLocation] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Conta aguardando código TOTP para concluir a troca (null = sem diálogo).
  const [totpTarget, setTotpTarget] = useState<number | null>(null);
  const [totpCode, setTotpCode] = useState("");

  const { data: me } = trpc.auth.me.useQuery();
  const { data: links } = trpc.accountLinks.list.useQuery(undefined, {
    enabled: menuOpen,
    refetchOnWindowFocus: false,
  });

  const switchMut = trpc.accountLinks.switchAccount.useMutation({
    onSuccess: (res) => {
      toast.success(
        res.tenantName ? `Trocando para ${res.tenantName}…` : "Trocando de conta…"
      );
      window.location.href = "/";
    },
    onError: (err, vars) => {
      if (err.message === "TOTP_REQUIRED") {
        setTotpCode("");
        setTotpTarget(vars.targetUserId);
        return;
      }
      toast.error(toUserFriendlyError(err));
    },
  });

  const tenantName = me?.tenantName;
  if (!tenantName) return null;

  const accounts = links?.accounts ?? [];
  const others = accounts.filter((a) => !a.isCurrent);

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 w-full rounded-lg px-2 py-1.5 text-left hover:bg-sidebar-accent transition-colors focus:outline-none"
            aria-label="Trocar de workspace"
          >
            <Building2 className="h-4 w-4 shrink-0 text-sidebar-foreground/70" />
            <span className="flex-1 min-w-0 truncate text-xs font-medium text-sidebar-foreground">
              {tenantName}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Workspace atual
          </DropdownMenuLabel>
          <DropdownMenuItem disabled className="opacity-100">
            <Check className="mr-2 h-4 w-4 text-primary" />
            <span className="truncate font-medium">{tenantName}</span>
          </DropdownMenuItem>

          {others.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Trocar para
              </DropdownMenuLabel>
              {others.map((account) => (
                <DropdownMenuItem
                  key={account.userId}
                  disabled={!account.canSwitch || switchMut.isPending}
                  onClick={() => switchMut.mutate({ targetUserId: account.userId })}
                  className="cursor-pointer"
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm">
                      {account.tenantName ?? "(sem workspace)"}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {account.email}
                      {account.blockedReason &&
                        ` · ${BLOCKED_LABEL[account.blockedReason] ?? "indisponível"}`}
                    </p>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setLocation("/contas-vinculadas")}
            className="cursor-pointer"
          >
            <Link2 className="mr-2 h-4 w-4" />
            <span>
              {others.length > 0 ? "Gerenciar contas vinculadas" : "Vincular outra conta…"}
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={totpTarget !== null}
        onOpenChange={(open) => {
          if (!open) setTotpTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Verificação em duas etapas</DialogTitle>
            <DialogDescription>
              A conta de destino tem verificação em duas etapas. Informe o
              código do seu aplicativo autenticador para concluir a troca.
            </DialogDescription>
          </DialogHeader>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            autoFocus
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTotpTarget(null)}>
              Cancelar
            </Button>
            <Button
              disabled={totpCode.length < 6 || switchMut.isPending}
              onClick={() => {
                if (totpTarget !== null) {
                  switchMut.mutate({ targetUserId: totpTarget, totpCode });
                }
              }}
            >
              {switchMut.isPending ? "Verificando…" : "Trocar de workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
